import { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { services } from "@/data/siteContent";

const inputClass =
  "bg-white/[0.03] border border-white/10 text-cream placeholder:text-cream-muted/60 focus-visible:border-[#D4AF37] focus-visible:ring-0 h-12 rounded-lg";

export default function Booking() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [service, setService] = useState("");
  const [date, setDate] = useState(null);
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name || !phone || !service || !date) {
      toast.error("Please complete name, phone, service and preferred date.");
      return;
    }
    setSubmitted(true);
    toast.success("Consultation request received. We'll be in touch soon.");
  };

  const reset = () => {
    setName("");
    setPhone("");
    setEmail("");
    setService("");
    setDate(null);
    setMessage("");
    setSubmitted(false);
  };

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="section relative"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(ellipse 50% 40% at 50% 0%, rgba(212,175,55,0.18), transparent 60%)",
        }}
      />
      <div className="container-luxe relative">
        <div className="reveal max-w-2xl mx-auto text-center">
          <div className="text-[11px] tracking-[0.4em] uppercase text-gold-light">
            Appointment · मुलाकात
          </div>
          <h2
            data-testid="booking-title"
            className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05] text-cream"
          >
            Book your{" "}
            <span className="italic font-editorial gold-text">
              consultation
            </span>
            .
          </h2>
          <p className="mt-5 text-cream-muted">
            Share a few details below and Jyotish ji&apos;s office will
            confirm your appointment at the earliest.
          </p>
        </div>

        <div className="reveal mt-14 max-w-3xl mx-auto glass-strong rounded-3xl p-8 sm:p-12">
          {submitted ? (
            <div
              data-testid="booking-success"
              className="text-center py-8"
            >
              <div className="w-20 h-20 mx-auto rounded-full gold-border grid place-items-center">
                <CheckCircle2
                  className="w-10 h-10 text-gold"
                  strokeWidth={1.2}
                />
              </div>
              <h3 className="mt-6 font-display text-3xl gold-text">
                Thank you, {name.split(" ")[0]}
              </h3>
              <p className="mt-4 text-cream-muted max-w-md mx-auto">
                Your request for{" "}
                <span className="text-gold">{service}</span> on{" "}
                <span className="text-gold">
                  {date ? format(date, "PPP") : ""}
                </span>{" "}
                has been received. Our office will contact you on{" "}
                <span className="text-gold">{phone}</span> shortly to confirm
                the exact time.
              </p>
              <p className="mt-3 font-devanagari text-cream-muted">
                जय अम्बे ज्योतिष जी आपसे शीघ्र संपर्क करेंगे।
              </p>
              <Button
                data-testid="booking-new-btn"
                onClick={reset}
                variant="outline"
                className="mt-8 gold-border bg-transparent text-gold hover:bg-[#D4AF37] hover:text-[#050B14] rounded-full px-8 h-11"
              >
                Book another
              </Button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-testid="booking-form"
              className="grid sm:grid-cols-2 gap-5"
            >
              <div>
                <Label className="text-cream text-[11px] tracking-[0.22em] uppercase">
                  Full Name
                </Label>
                <Input
                  data-testid="booking-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your full name"
                  className={`${inputClass} mt-2`}
                />
              </div>
              <div>
                <Label className="text-cream text-[11px] tracking-[0.22em] uppercase">
                  Phone / WhatsApp
                </Label>
                <Input
                  data-testid="booking-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+91 XXXXX XXXXX"
                  className={`${inputClass} mt-2`}
                />
              </div>
              <div>
                <Label className="text-cream text-[11px] tracking-[0.22em] uppercase">
                  Email (optional)
                </Label>
                <Input
                  data-testid="booking-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={`${inputClass} mt-2`}
                />
              </div>
              <div>
                <Label className="text-cream text-[11px] tracking-[0.22em] uppercase">
                  Service
                </Label>
                <Select value={service} onValueChange={setService}>
                  <SelectTrigger
                    data-testid="booking-service"
                    className={`${inputClass} mt-2`}
                  >
                    <SelectValue placeholder="Choose a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A1428] border-[#D4AF37]/25 text-cream">
                    {services.map((s) => (
                      <SelectItem
                        key={s.key}
                        value={s.title}
                        data-testid={`booking-service-option-${s.key}`}
                        className="focus:bg-[#D4AF37]/15 focus:text-gold-light"
                      >
                        {s.title} · {s.titleHi}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-cream text-[11px] tracking-[0.22em] uppercase">
                  Preferred Date
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      data-testid="booking-date-trigger"
                      variant="outline"
                      className={`${inputClass} mt-2 w-full justify-start font-normal hover:bg-white/[0.05] hover:text-cream`}
                    >
                      <CalendarIcon className="w-4 h-4 mr-2 text-gold" />
                      {date ? format(date, "PPP") : (
                        <span className="text-cream-muted/60">
                          Pick a date
                        </span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#0A1428] border-[#D4AF37]/25">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                      data-testid="booking-calendar"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-cream text-[11px] tracking-[0.22em] uppercase">
                  Your Question (optional)
                </Label>
                <Textarea
                  data-testid="booking-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Briefly share what you seek clarity on..."
                  rows={4}
                  className={`${inputClass} mt-2 h-auto resize-none py-3`}
                />
              </div>
              <div className="sm:col-span-2 flex justify-center pt-2">
                <Button
                  type="submit"
                  data-testid="booking-submit"
                  className="rounded-full px-10 h-12 bg-[#D4AF37] text-[#050B14] hover:bg-[#F3E5AB] font-semibold text-[12px] tracking-[0.24em] uppercase gold-glow"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Request Appointment
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
